import { AppIdvAcceptableDocumentsResponseSchema } from "@amzn/client-workbench-api-model";
import { Instructions } from "@amzn/itsvideo-recorder/lib/VideoRecorder";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col } from "@amzn/stencil-react-components/layout";
import { Select, SelectLayout } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Video from "@/components/Commons/Video/Video";
import VideoRecorderCustom from "@/components/Commons/VideoRecorderCustom";
import { MediaCheckWrapper } from "@/components/Commons/VideoRecorderCustom/MediaCheckWrapper";
import {
  APP_CONFIG_IDV_RULES_DEFAULT_KEY,
  COUNTRY_CODES_NAME_MAP,
  VideoGuidanceName,
  VIDEOS_PLAYBACK_CONFIGS,
} from "@/components/constants";
import DosDonts from "@/components/IdentityCheck/DosDonts";
import { useIdvAcceptableDocs } from "@/hooks/useIdvAcceptableDocs";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

import ErrorBanner from "../Commons/ErrorBanner";
import { StepBodyStyledText } from "../Commons/StepBodyStyledText";
import { ChatWithITSupportFlyoutLink } from "../GetSupport";
import { RecordIdStepProps } from "./FullIdVerification";

/**
 * All valid primary IDs accepted by the CWB verification API
 */
export type PrimaryIdType = string;

// Documents that users are eligible to use with both their code and user-friendly name
type Document = {
  code: string;
  userFriendlyName: string;
};

// Country codes mapped to documents that users are eligible to use
type CountryToIdMapping = {
  [key: string]: Document[];
};

/**
 * View for uploading NH's primary ID.
 */
export default function PrimaryId({
  clearSelectedStep,
  fullIdVerificationElementRef,
}: RecordIdStepProps): JSX.Element {
  const intl = useIntl();
  const { dispatch, state } = useContext(AppContext);
  const [selectedIdCountry, setSelectedIdCountry] = useState<string>("");
  const [selectedIdType, setSelectedIdType] = useState<Document>({
    code: "",
    userFriendlyName: "",
  });

  const {
    data: idvAcceptableDocs,
    isLoading: isIdvDocsLoading,
    isError: isIdvDocsError,
  } = useIdvAcceptableDocs();

  const requiredDocuments = state.requiredDocsWithGesturesCount - 1;

  /**
   * Custom sorting function to sort primaryIdCountryCodes by their user-friendly name rather than their codes.
   * For example, ARE is United Arab Emirates so should be towards the bottom of the list rather than the top.
   */
  const sortByCountryName = (a: string, b: string) => {
    const nameA =
      COUNTRY_CODES_NAME_MAP[a as keyof typeof COUNTRY_CODES_NAME_MAP];
    const nameB =
      COUNTRY_CODES_NAME_MAP[b as keyof typeof COUNTRY_CODES_NAME_MAP];
    return new Intl.Collator().compare(nameA, nameB);
  };

  const primaryIdCountryCodes = useMemo(
    () =>
      idvAcceptableDocs
        ? Object.keys(idvAcceptableDocs)
            .filter(
              (code) =>
                code !== APP_CONFIG_IDV_RULES_DEFAULT_KEY && code !== "YYY"
            )
            .sort(sortByCountryName)
        : [],
    [idvAcceptableDocs]
  );

  const instructions: Instructions[] = [
    {
      text: intl.formatMessage(
        {
          id: "identityCheck-primaryId-video-instruction",
        },
        { requiredDocuments }
      ),
      bannerBackGroundColor: "primary80",
      displayTimestampMs: 0,
    },
    {
      text: intl.formatMessage(
        {
          id: "identityCheck-primaryId-video-instruction2",
        },
        { requiredDocuments }
      ),
      bannerBackGroundColor: "pink70",
      displayTimestampMs: 5_000,
    },
  ];

  /**
   * Convert the response from the acceptable documents API response into an easier to use format for rendering
   * @param acceptableDocsResponse The acceptable documents API response to convert into the desired format
   */
  const countryToIdMappingHelper = (
    acceptableDocsResponse: AppIdvAcceptableDocumentsResponseSchema
  ): CountryToIdMapping => {
    const countryToIdMapping: CountryToIdMapping = {};

    Object.keys(acceptableDocsResponse).forEach((countryCode) => {
      const documentsAllowed =
        acceptableDocsResponse[countryCode].documentsAllowed ?? [];

      // Transform documentsAllowed to the desired format
      const documentsList: Document[] = documentsAllowed.map((doc) => {
        const [code, userFriendlyName] = Object.entries(doc)[0];
        return { code, userFriendlyName };
      });

      // Add to the mapping
      countryToIdMapping[countryCode] = documentsList;
    });
    return countryToIdMapping;
  };

  // Initialize the primaryIdCountry on first render only once since default value will be populated with user's country from /user/progress
  useEffect(() => {
    if (state.hiringCountry) {
      trackStructEvent({
        category: Day1EventCategory.IDCheckFullPri,
        action: Day1EventAction.SelectCountry,
        label: state.hiringCountry,
      });
      setSelectedIdCountry(state.hiringCountry);
    }
  }, []);

  const countryToIdMapping = useMemo(
    () =>
      idvAcceptableDocs ? countryToIdMappingHelper(idvAcceptableDocs) : [],
    [idvAcceptableDocs]
  );
  const acceptableIdTypes = countryToIdMapping
    ? (countryToIdMapping as CountryToIdMapping)[selectedIdCountry]
    : [];

  /**
   * Render the primary ID country options.
   * @param country The primary ID country option
   */
  const renderIdCountryOption = (
    countryCodeOption: string
  ): string | undefined => {
    return COUNTRY_CODES_NAME_MAP[
      countryCodeOption as keyof typeof COUNTRY_CODES_NAME_MAP
    ];
  };

  /**
   * Render the primary ID type options.
   * @param primaryIdTypeCode The primary ID option
   */
  const renderIdTypeOption = (idTypeOption: Document) => {
    return idTypeOption.userFriendlyName;
  };

  /**
   * Render the primary ID type selected value. This is needed because sometimes the selectedIdType needs to be reset to "" which will only be tracked by selectedIdType
   * @param primaryIdTypeCode The primary ID option
   */
  const renderIdTypeSelectedValue = () => {
    return selectedIdType.userFriendlyName;
  };

  /**
   * Set the selected primary ID type
   * @param option The primary ID option
   */
  const handleIdOnChange = (option: Document) => {
    trackStructEvent({
      category: Day1EventCategory.IDCheckFullPri,
      action: Day1EventAction.SelectPrimaryId,
      label: option.code,
    });
    dispatch({
      type: ActionType.SET_PRIMARY_ID_SELECTION,
      primaryIdSelection: {
        issuingCountry: selectedIdCountry,
        documentType: option.code,
      },
    });
    setSelectedIdType({
      code: option.code,
      userFriendlyName: option.userFriendlyName,
    });
  };

  /**
   * Set the selected primary ID country
   * @param option The primary ID country option
   */
  const handleCountryOnChange = (option: string) => {
    trackStructEvent({
      category: Day1EventCategory.IDCheckFullPri,
      action: Day1EventAction.SelectCountry,
      label: option,
    });
    dispatch({
      type: ActionType.SET_PRIMARY_ID_SELECTION,
      primaryIdSelection: {
        issuingCountry: option,
        documentType: "",
      },
    });
    setSelectedIdCountry(option);
    // reset the selected ID after the new hire chooses a new country so there is no country and ID mismatch
    setSelectedIdType({ code: "", userFriendlyName: "" });
  };

  if (isIdvDocsLoading) {
    return (
      <Spinner dataTestId="spinnerTestId" showText size={SpinnerSize.Medium} />
    );
  }
  if (isIdvDocsError) {
    return (
      <ErrorBanner>
        <FormattedMessage
          id="error-somethingWentWrong"
          values={{
            chatWithITSupportLink: (
              <ChatWithITSupportFlyoutLink localeMessageId="text-chatWithITSupport" />
            ),
          }}
        />
      </ErrorBanner>
    );
  }
  return (
    <Col gridGap="S300">
      <Col gridGap="S400" dataTestId="primaryIdIntroCol">
        <StepBodyStyledText>
          <Text fontWeight="medium">
            <FormattedMessage
              id="identityCheck-full-steps-recordPrimaryId"
              values={{
                requiredDocuments,
              }}
            />
          </Text>
        </StepBodyStyledText>
        <StepBodyStyledText>
          <Text fontWeight="regular">
            <FormattedMessage
              id="identityCheck-primaryId-instruction"
              values={{
                requiredDocuments,
              }}
            />
          </Text>
        </StepBodyStyledText>
      </Col>
      <Col gridGap="S200">
        <InputWrapper
          labelText={intl.formatMessage(
            {
              id: "identityCheck-primaryIdCountry-select-label",
            },
            { requiredDocuments }
          )}
          id="primaryIdCountryLabel"
          required
        >
          {(inputProps) => (
            <Select
              {...inputProps}
              width="50%"
              labelId="primaryIdCountryLabel"
              id="primaryIdCountryLabel"
              options={primaryIdCountryCodes}
              // Initialize default value as user-friendly name of user's hiring country from /user/progress
              defaultValue={renderIdCountryOption}
              // Placeholder in case the defaultValue is not yet initialized
              placeholder={intl.formatMessage(
                {
                  id: "identityCheck-primaryId-country-select-placeholder",
                },
                { requiredDocuments }
              )}
              layout={SelectLayout.Custom}
              aria-required={true}
              onChange={handleCountryOnChange}
              dataTestId="selectIdCountryDataTestId"
              value={selectedIdCountry}
              renderOption={renderIdCountryOption}
            />
          )}
        </InputWrapper>
      </Col>
      {selectedIdCountry && (
        <Col gridGap="S200">
          <InputWrapper
            labelText={intl.formatMessage(
              {
                id: "identityCheck-primaryId-select-label",
              },
              { requiredDocuments }
            )}
            id="primaryIdLabel"
            required
          >
            {(inputProps) => (
              <Select
                {...inputProps}
                width="50%"
                labelId="primaryIdLabel"
                id="primaryIdLabel"
                options={acceptableIdTypes}
                placeholder={intl.formatMessage(
                  {
                    id: "identityCheck-primaryId-select-placeholder",
                  },
                  { requiredDocuments }
                )}
                layout={SelectLayout.Custom}
                aria-required={true}
                onChange={handleIdOnChange}
                dataTestId="selectIdTypeDataTestId"
                value={selectedIdType.userFriendlyName}
                renderOption={renderIdTypeOption}
                renderSelectedValue={renderIdTypeSelectedValue}
              />
            )}
          </InputWrapper>
        </Col>
      )}
      <DosDonts isIdPresent />
      <Video
        videoFileConfig={
          VIDEOS_PLAYBACK_CONFIGS[VideoGuidanceName.PRIMARY_ID_DESKTOP]
        }
        overlayTextI18nId="videoGuidance-example"
      />
      <MediaCheckWrapper>
        <VideoRecorderCustom
          clearSelectedStep={clearSelectedStep}
          fullIdVerificationElementRef={fullIdVerificationElementRef}
          recordCompletedI18nId={
            state.requiredDocsWithGesturesCount >= 3
              ? "identityCheck-primaryId-completed"
              : "identityCheck-governmentId-completed"
          }
          instructions={instructions}
          documentType="PRIMARY_ID_RECORDING"
          eventCategory={Day1EventCategory.IDCheckFullPri}
          refPage="ezo-identity-check-full-pri"
        />
      </MediaCheckWrapper>
    </Col>
  );
}
